import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Wordmark } from "@/components/Logo";
import { MaskedLines, EASE } from "@/components/Motion";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { useSeo } from "@/lib/seo";

const WHY = [
  ["Open to everyone", "Sign up with any email address. If you use a company email, teammates on the same domain automatically share one company profile."],
  ["Find the right partner faster", "Search brands, events, creators, agencies, venues, sports teams and media companies by industry, location and budget, and browse open briefs and sponsorship packages."],
  ["Run every deal in one place", "Track partnerships from first contact to signed contract, message partners, share files, sign agreements, send invoices and get paid by card, cash or crypto."],
];

function Why() {
  return (
    <section className="mt-14 border-t border-[#2A1240]/10 pt-10 space-y-6" data-testid="auth-why">
      <h2 className="kicker">Why teams use Balqis Book</h2>
      {WHY.map(([h, t]) => (
        <div key={h}>
          <h3 className="text-[#2A1240]">{h}</h3>
          <p className="mt-1 text-sm text-slate-300">{t}</p>
        </div>
      ))}
    </section>
  );
}

const TYPES = ["Brand", "Sponsor", "Event", "Creator", "Agency", "Publisher", "Venue", "Sports Team", "Media Company", "Nonprofit", "Investor", "Hospitality Group"];

function Shell({ title, sub, children }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FFF7FB]">
      <div className="relative hidden lg:block overflow-hidden">
        <motion.img src="/img/summit.jpg" alt="Partnership summit audience" initial={{ scale: 1.2 }} animate={{ scale: 1 }} transition={{ duration: 2, ease: EASE }} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#FFF7FB] via-[#FFF7FB]/70 to-transparent" />
        <div className="absolute bottom-14 left-14 right-14">
          <p className="kicker">For marketing and partnerships teams</p>
          <p className="mt-4 font-display text-4xl text-[#2A1240] leading-tight">Find your next partner here.</p>
        </div>
      </div>
      <div className="flex flex-col px-6 sm:px-14 py-10">
        <Link to="/" data-testid="auth-home-link"><Wordmark /></Link>
        <div className="my-auto py-12 w-full max-w-md mx-auto">
          <MaskedLines as="h1" className="font-display text-4xl sm:text-5xl text-[#2A1240]" lines={[title]} />
          <p className="mt-3 text-slate-300">{sub}</p>
          {children}
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, ...p }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-[0.16em] text-slate-400">{label}</span>
    <input {...p} className="field mt-2" />
  </label>
);

export function Login() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useSeo("Sign in", "Sign in to Balqis Book to manage partnerships, deals, contracts and payments.", "/login");
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      nav(sp.get("next") || "/app");
    } catch (e2) {
      setErr(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Shell title="Welcome back." sub="Sign in with your email.">
      <form onSubmit={submit} className="mt-10 space-y-5" data-testid="login-form">
        <Field label="Email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="login-email-input" />
        <Field label="Password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" />
        <div className="-mt-2 text-right"><Link to={`/forgot-password${email ? `?email=${encodeURIComponent(email)}` : ""}`} className="text-xs text-slate-300 hover:text-[#8E2BC4] underline underline-offset-4" data-testid="login-forgot-link">Forgot password?</Link></div>
        {err && <p className="text-sm text-red-300" data-testid="login-error">{err}</p>}
        <button className="btn-gold w-full !py-4" disabled={busy} data-testid="login-submit-button">{busy ? "Signing in…" : "Sign in"}</button>
        <p className="text-sm text-slate-400">New team? <Link to={`/register${sp.get("next") ? `?next=${encodeURIComponent(sp.get("next"))}` : ""}`} className="text-[#8E2BC4] underline underline-offset-4" data-testid="login-register-link">Join with your work email</Link></p>
      </form>
      <Why />
    </Shell>
  );
}

export function ForgotPassword() {
  const [sp] = useSearchParams();
  const [email, setEmail] = useState(sp.get("email") || "");
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try { await api.post("/auth/forgot-password", { email }); setSent(true); } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Forgot password?" sub="Enter your email and we'll send you a reset link.">
      {sent ? (
        <div className="mt-10 rounded-2xl border border-[#E0218A]/30 bg-[#E0218A]/10 p-6" data-testid="forgot-sent">
          <p className="text-[#2A1240]">Check your inbox.</p>
          <p className="mt-2 text-sm text-slate-300">If an account exists for <span className="text-[#8E2BC4]">{email}</span>, a reset link is on its way. It works once and expires in 1 hour.</p>
          <Link to="/login" className="btn-ghost mt-6 inline-flex" data-testid="forgot-back-login">Back to sign in</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-10 space-y-5" data-testid="forgot-form">
          <Field label="Email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="forgot-email-input" />
          {err && <p className="text-sm text-red-300" data-testid="forgot-error">{err}</p>}
          <button className="btn-gold w-full !py-4" disabled={busy} data-testid="forgot-submit-button">{busy ? "Sending…" : "Send reset link"}</button>
          <p className="text-sm text-slate-400">Remembered it? <Link to="/login" className="text-[#8E2BC4] underline underline-offset-4" data-testid="forgot-login-link">Sign in</Link></p>
        </form>
      )}
    </Shell>
  );
}

export function ResetPassword() {
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 8) { setErr("Use at least 8 characters."); return; }
    if (pw !== pw2) { setErr("Passwords don't match."); return; }
    setBusy(true);
    try { await api.post("/auth/reset-password", { token, password: pw }); setDone(true); } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Choose a new password." sub="Make it at least 8 characters.">
      {!token ? (
        <p className="mt-10 text-sm text-red-300" data-testid="reset-missing-token">This reset link is incomplete. <Link to="/forgot-password" className="underline">Request a new one</Link>.</p>
      ) : done ? (
        <div className="mt-10 rounded-2xl border border-emerald-400/30 bg-emerald-400/10 p-6" data-testid="reset-done">
          <p className="text-[#2A1240]">Password updated.</p>
          <p className="mt-2 text-sm text-slate-300">You can now sign in with your new password.</p>
          <Link to="/login" className="btn-gold mt-6 inline-flex" data-testid="reset-login-link">Sign in</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-10 space-y-5" data-testid="reset-form">
          <Field label="New password" type="password" required minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-password-input" />
          <Field label="Confirm new password" type="password" required minLength={8} value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="reset-password-confirm-input" />
          {err && <p className="text-sm text-red-300" data-testid="reset-error">{err} {err.includes("expired") && <Link to="/forgot-password" className="underline">Request a new link</Link>}</p>}
          <button className="btn-gold w-full !py-4" disabled={busy} data-testid="reset-submit-button">{busy ? "Saving…" : "Save new password"}</button>
        </form>
      )}
    </Shell>
  );
}

export function Register() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [f, setF] = useState({ name: "", email: "", password: "", company_name: "", company_type: "Brand" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useSeo("Join Balqis Book", "Create a free Balqis Book account and start discovering brand partners, sponsors and events.", "/register");
  const inviteToken = sp.get("invite") || "";
  const [inv, setInv] = useState(null);
  useEffect(() => {
    if (!inviteToken) return;
    api.get(`/invites/${inviteToken}`).then((r) => { setInv(r.data); setF((x) => ({ ...x, email: r.data.email })); }).catch((e) => setErr(errMsg(e)));
  }, [inviteToken]);
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/register", { ...f, invite_token: inviteToken });
      setUser(data);
      nav(sp.get("next") || "/app/profile?welcome=1");
    } catch (e2) {
      setErr(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Shell title={inv ? `Join ${inv.company_name}.` : "Join Balqis Book."} sub={inv ? `${inv.invited_by} invited you${inv.job_title ? ` as ${inv.job_title}` : ""}.` : "Use any email. Teammates with the same company email domain join the same company profile."}>
      <form onSubmit={submit} className="mt-10 space-y-5" data-testid="register-form">
        <Field label="Full name" required value={f.name} onChange={set("name")} data-testid="register-name-input" />
        <Field label="Email" type="email" required value={f.email} onChange={set("email")} readOnly={!!inv} placeholder="you@example.com" data-testid="register-email-input" />
        <Field label="Password (8+ characters)" type="password" required minLength={8} value={f.password} onChange={set("password")} data-testid="register-password-input" />
        {!inv && <div className="grid grid-cols-2 gap-4">
          <Field label="Company name" value={f.company_name} onChange={set("company_name")} placeholder="Optional" data-testid="register-company-input" />
          <label className="block">
            <span className="text-xs uppercase tracking-[0.16em] text-slate-400">You are a</span>
            <select value={f.company_type} onChange={set("company_type")} className="field mt-2" data-testid="register-type-select">
              {TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
          </label>
        </div>}
        {err && <p className="text-sm text-red-300" data-testid="register-error">{err}</p>}
        <label className="flex items-start gap-3 text-sm text-slate-300" data-testid="register-terms-label">
          <input type="checkbox" required className="mt-1 h-4 w-4 accent-[#E0218A]" data-testid="register-terms-checkbox" />
          <span>I agree to the <Link to="/terms" target="_blank" className="text-[#8E2BC4] underline">Terms of Service</Link> and <Link to="/privacy" target="_blank" className="text-[#8E2BC4] underline">Privacy Policy</Link>.</span>
        </label>
        <button className="btn-gold w-full !py-4" disabled={busy} data-testid="register-submit-button">{busy ? "Creating…" : "Create account"}</button>
        <p className="text-sm text-slate-400">Already a member? <Link to="/login" className="text-[#8E2BC4] underline underline-offset-4" data-testid="register-login-link">Sign in</Link></p>
      </form>
      <Why />
    </Shell>
  );
}
